import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText,
  FileSignature,
  FileBadge,
  Download,
  Plus,
  Pencil,
  CheckCircle2,
  Circle,
  Trash2,
  User,
  UserCheck,
  History,
  Snowflake,
  CloudUpload,
} from "lucide-react";
import { Receipt } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CONTRACT_DOCS, isMinor } from "@/lib/contracts";
import AutorizacionDialog from "./AutorizacionDialog";
import { formatDateES } from "@/lib/utils-app";
import ContractDialog from "./ContractDialog";
import TutorDialog from "./TutorDialog";
import SignatureRequestDialog from "./SignatureRequestDialog";
import SignatureAuditDialog from "./SignatureAuditDialog";
import InvoiceCopyPanel from "./InvoiceCopyPanel";
import SyncSignedButton from "./SyncSignedButton";

const ICONS = { FileText, FileSignature, FileBadge };

/**
 * ContractsTab
 *
 * Dual-mode component:
 *  - Student mode: <ContractsTab student={fullStudent} />
 *      Loads only contracts of the given student, allows creating new ones,
 *      and reveals the "autorización menor" action when the student is a minor.
 *  - Global mode:  <ContractsTab />
 *      Loads ALL contracts, displays the owning student's name on each row,
 *      hides the "Nuevo contrato" button (need to pick a student first) and
 *      hides the "autorización menor" action (age unknown).
 *  Editing an existing contract is available in both modes.
 */
export default function ContractsTab({ student }) {
  const isStudentMode = !!student?.id;
  const [contracts, setContracts] = useState([]);
  const [studentsMap, setStudentsMap] = useState({}); // {id: {id,name}}
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tutorFor, setTutorFor] = useState(null); // {contractId}
  const [signatureFor, setSignatureFor] = useState(null); // full contract obj
  const [auditFor, setAuditFor] = useState(null); // full contract obj
  const [invoiceFor, setInvoiceFor] = useState(null); // V1.55 · ficha facturación
  const [newAutorizacion, setNewAutorizacion] = useState(null); // "autorizacion_dgt"|"autorizacion_menor"

  const minor = student ? isMinor(student.birth_date) : false;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = isStudentMode
        ? `/contracts?student_id=${student.id}`
        : "/contracts";
      const { data } = await api.get(url);
      setContracts(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [isStudentMode, student?.id]);

  useEffect(() => { load(); }, [load]);

  // Fetch student directory once in global mode so we can show names per row.
  useEffect(() => {
    if (isStudentMode) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/students");
        if (cancelled) return;
        const map = {};
        for (const s of data || []) map[s.id] = s;
        setStudentsMap(map);
      } catch (err) {
        // Non-blocking — we'll just fall back to showing the id.
        console.warn("students fetch failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [isStudentMode]);

  const studentNameFor = useMemo(
    () => (sid) => studentsMap[sid]?.name || sid || "—",
    [studentsMap]
  );

  const [uploadingDrive, setUploadingDrive] = useState("");
  const uploadToOneDrive = async (c) => {
    setUploadingDrive(c.id);
    try {
      const { data } = await api.post(`/contracts/${c.id}/upload-onedrive`);
      toast.success(data.deduplicated
        ? "Ya estaba en OneDrive (misma versión) — nada que subir."
        : "Documento subido a OneDrive (carpeta CONTRATOS).");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setUploadingDrive("");
    }
  };

  const downloadPdf = async (contractId, docKey, label) => {
    try {
      const res = await api.get(`/contracts/${contractId}/pdf/${docKey}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || `Error generando ${label}`);
    }
  };

  const downloadAutorizacionMenor = async (tutor) => {
    if (!tutorFor) return;
    try {
      const res = await api.post(
        `/contracts/${tutorFor}/pdf/autorizacion_menor`,
        tutor,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      setTutorFor(null);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const toggleSigned = async (c) => {
    try {
      await api.patch(`/contracts/${c.id}`, { signed: !c.signed });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar contrato ${c.numero}? Esta acción es irreversible.`)) return;
    try {
      await api.delete(`/contracts/${c.id}`);
      toast.success("Contrato eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  // Which contract docs to expose per row.
  // In student mode: filter out "needsTutor" unless minor.
  // In global mode: hide "needsTutor" (age unknown; user should open the student ficha instead).
  const docsForRow = CONTRACT_DOCS.filter((d) => {
    if (!d.needsTutor) return true;
    return isStudentMode && minor;
  });

  return (
    <div className="space-y-4" data-testid="contracts-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-bold flex items-center gap-2">
          <FileText className="h-5 w-5 text-orange-500" />
          {isStudentMode ? "Contratos y autorizaciones del alumno" : "Todos los contratos"}
        </h3>
        <SyncSignedButton studentId={isStudentMode ? student.id : null} onDone={load} />
        {isStudentMode && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              data-testid="contracts-new-btn"
              className="btn-primary !h-9 !px-3 text-sm"
            >
              <Plus className="h-4 w-4" />
              Nuevo contrato
            </button>
            <button
              type="button"
              onClick={() => setNewAutorizacion("autorizacion_dgt")}
              data-testid="new-autorizacion-dgt-btn"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
            >
              <Plus className="h-4 w-4" />
              Autorización DGT
            </button>
            <button
              type="button"
              onClick={() => setNewAutorizacion("autorizacion_menor")}
              data-testid="new-autorizacion-menor-btn"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-sm font-semibold bg-fuchsia-600 hover:bg-fuchsia-700 text-white transition-colors"
            >
              <Plus className="h-4 w-4" />
              Autorización menor
            </button>
          </div>
        )}
      </div>

      {isStudentMode && <InvoiceCopyPanel student={student} contracts={contracts} />}

      {!isStudentMode && (
        <p className="text-xs text-neutral-500">
          Para crear un contrato nuevo, abre la ficha del alumno correspondiente y pulsa <strong>Nuevo contrato</strong>.
        </p>
      )}

      {loading ? (
        <div className="text-sm text-neutral-500">Cargando…</div>
      ) : contracts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center text-sm text-neutral-500">
          {isStudentMode
            ? <>Este alumno aún no tiene contratos. Pulsa <strong>Nuevo contrato</strong> para crear uno.</>
            : <>No hay contratos registrados todavía.</>}
        </div>
      ) : (
        <div className="space-y-3">
          {contracts.map((c) => (
            <div key={c.id} className="card-surface p-3 space-y-3" data-testid={`contract-row-${c.id}`}>
              <div className="flex items-start gap-2 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <div className="font-mono font-bold text-sm inline-flex items-center gap-1.5">
                    {c.numero}
                    {c.document_kind && c.document_kind !== "contract" && (
                      <span
                        className={
                          "text-[9px] uppercase font-sans font-bold rounded px-1.5 py-0.5 " +
                          (c.document_kind === "autorizacion_dgt"
                            ? "bg-indigo-100 text-indigo-800"
                            : "bg-fuchsia-100 text-fuchsia-800")
                        }
                        data-testid={`contract-kind-${c.id}`}
                      >
                        {c.document_kind === "autorizacion_dgt" ? "Autorización DGT" : "Autorización menor"}
                      </span>
                    )}
                  </div>
                  {!isStudentMode && (
                    <div
                      className="text-xs text-neutral-700 flex items-center gap-1 mt-0.5"
                      data-testid={`contract-student-${c.id}`}
                    >
                      <User className="h-3.5 w-3.5 text-neutral-400" />
                      {studentNameFor(c.student_id)}
                    </div>
                  )}
                  <div className="text-xs text-neutral-500">
                    {formatDateES(c.fecha)}
                    {c.document_kind === "autorizacion_dgt" || c.document_kind === "autorizacion_menor"
                      ? null
                      : <> · Permiso {c.permiso} · Total {Number(c.total || 0).toFixed(2)} €</>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleSigned(c)}
                  data-testid={`contract-toggle-signed-${c.id}`}
                  disabled={!!c.frozen}
                  title={c.frozen ? "Congelado tras firma remota" : ""}
                  className={
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors " +
                    (c.signed
                      ? "bg-green-100 text-green-800 hover:bg-green-200"
                      : "bg-amber-100 text-amber-800 hover:bg-amber-200") +
                    (c.frozen ? " cursor-not-allowed opacity-80" : "")
                  }
                >
                  {c.frozen ? <Snowflake className="h-3.5 w-3.5" /> : c.signed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                  {c.frozen ? "Congelado" : c.signed ? "Firmado" : "Sin firmar"}
                </button>
                <button
                  type="button"
                  onClick={() => setSignatureFor(c)}
                  data-testid={`contract-sign-${c.id}`}
                  className="btn-ghost border border-orange-200 text-orange-700 hover:bg-orange-50 !h-8 !px-2 text-xs"
                  title="Solicitar firma remota"
                >
                  <UserCheck className="h-3.5 w-3.5" /> Firma remota
                </button>
                <button
                  type="button"
                  onClick={() => setAuditFor(c)}
                  data-testid={`contract-audit-${c.id}`}
                  className="btn-ghost border border-neutral-200 !h-8 !px-2 text-xs"
                  title="Ver historial y auditoría"
                >
                  <History className="h-3.5 w-3.5" /> Historial
                </button>
                <button
                  type="button"
                  onClick={() => uploadToOneDrive(c)}
                  disabled={uploadingDrive === c.id}
                  data-testid={`contract-upload-onedrive-${c.id}`}
                  className="btn-ghost border border-sky-200 text-sky-700 hover:bg-sky-50 !h-8 !px-2 text-xs disabled:opacity-50"
                  title="Subir este documento a OneDrive (carpeta CONTRATOS del expediente)"
                >
                  <CloudUpload className="h-3.5 w-3.5" /> OneDrive
                </button>
                <button
                  type="button"
                  onClick={() => setInvoiceFor(c)}
                  data-testid={`contract-invoice-${c.id}`}
                  className="btn-ghost border border-emerald-200 text-emerald-700 hover:bg-emerald-50 !h-8 !px-2 text-xs"
                  title="Ficha de facturación (copia/pega)"
                >
                  <Receipt className="h-3.5 w-3.5" /> Factura
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(c)}
                  data-testid={`contract-edit-${c.id}`}
                  disabled={!!c.frozen}
                  title={c.frozen ? "Contrato congelado" : ""}
                  className={
                    "btn-ghost border border-neutral-200 !h-8 !px-2 text-xs " +
                    (c.frozen ? "opacity-50 cursor-not-allowed" : "")
                  }
                >
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </button>
                <button
                  type="button"
                  onClick={() => remove(c)}
                  data-testid={`contract-delete-${c.id}`}
                  disabled={!!c.frozen}
                  title={c.frozen ? "Contrato congelado" : ""}
                  className={
                    "btn-ghost border border-red-200 text-red-600 hover:bg-red-50 !h-8 !px-2 text-xs " +
                    (c.frozen ? "opacity-50 cursor-not-allowed" : "")
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {docsForRow.map((d) => {
                  const Ic = ICONS[d.icon] || FileText;
                  // V1.37.12 · Etiqueta dinámica según el tipo del documento
                  const isAut = c.document_kind === "autorizacion_dgt" || c.document_kind === "autorizacion_menor";
                  const btnLabel = isAut
                    ? (c.document_kind === "autorizacion_dgt" ? "Descargar autorización DGT" : "Descargar autorización menor")
                    : d.label;
                  if (d.needsTutor) {
                    return (
                      <button
                        key={d.key}
                        type="button"
                        onClick={() => setTutorFor(c.id)}
                        data-testid={`contract-pdf-${d.key}-${c.id}`}
                        className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 hover:bg-blue-100 px-3 py-2 text-xs font-semibold transition-colors"
                      >
                        <Ic className="h-4 w-4" />
                        <span className="flex-1 text-left">{d.label}</span>
                        <Download className="h-3.5 w-3.5" />
                      </button>
                    );
                  }
                  return (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => downloadPdf(c.id, d.key, btnLabel)}
                      data-testid={`contract-pdf-${d.key}-${c.id}`}
                      className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 px-3 py-2 text-xs font-semibold transition-colors"
                    >
                      <Ic className={"h-4 w-4 " + (isAut ? "text-indigo-500" : "text-orange-500")} />
                      <span className="flex-1 text-left">{btnLabel}</span>
                      <Download className="h-3.5 w-3.5 text-neutral-500" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <ContractDialog
        open={showCreate || !!editing}
        studentId={student?.id || editing?.student_id || null}
        contract={editing}
        onClose={() => { setShowCreate(false); setEditing(null); }}
        onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
      />

      <TutorDialog
        open={!!tutorFor}
        onClose={() => setTutorFor(null)}
        onConfirm={downloadAutorizacionMenor}
      />

      <SignatureRequestDialog
        open={!!signatureFor}
        contract={signatureFor}
        student={isStudentMode ? student : studentsMap[signatureFor?.student_id] || null}
        onClose={() => setSignatureFor(null)}
        onChanged={load}
      />

      <SignatureAuditDialog
        open={!!auditFor}
        contract={auditFor}
        onClose={() => setAuditFor(null)}
      />

      <AutorizacionDialog
        open={!!newAutorizacion}
        onClose={() => setNewAutorizacion(null)}
        studentId={student?.id || null}
        kind={newAutorizacion}
        students={student ? [student] : Object.values(studentsMap)}
        onCreated={() => { setNewAutorizacion(null); load(); }}
      />

      <Dialog open={!!invoiceFor} onOpenChange={(o) => !o && setInvoiceFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>Facturación · {studentNameFor(invoiceFor?.student_id)}</DialogTitle>
          </DialogHeader>
          {invoiceFor && (
            <InvoiceCopyPanel
              student={isStudentMode ? student : studentsMap[invoiceFor.student_id]}
              contracts={[invoiceFor]}
              defaultOpen
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
